import React from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import type {
  EventContactPair,
  EventFormPayload,
} from "@/services/api/user/events";

export const MAX_TAGS = 20;

type ContactField = "phoneList" | "emailList" | "linkList";

interface EventFormInfoStepProps {
  payload: EventFormPayload;
  onChange: <K extends keyof EventFormPayload>(
    key: K,
    value: EventFormPayload[K],
  ) => void;
  /** Editing an existing event: the backend edit route drops new hash tags. */
  tagsReadOnly?: boolean;
}

const inputClass =
  "w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 px-3.5 text-[13px] text-gray-800 outline-none transition focus:border-[#ffa217] focus:bg-white";
const labelClass = "mb-1.5 block text-[13px] font-semibold text-gray-500";

const emptyContact: EventContactPair = { input1: "", input2: "" };

/**
 * Step 2 of the event wizard — title, description, location, contact
 * details and hash tags. Mirrors the RN `EventInformationScreen`.
 */
const EventFormInfoStep: React.FC<EventFormInfoStepProps> = ({
  payload,
  onChange,
  tagsReadOnly = false,
}) => {
  const { t } = useTranslation();

  const setField = (key: ContactField, index: number, value: string, slot: 1 | 2) => {
    const list = payload[key];
    onChange(key, list.map((item, i) =>
      i === index ? { ...item, [slot === 1 ? "input1" : "input2"]: value } : item,
    ));
  };

  const addContact = (key: ContactField) => {
    onChange(key, [...payload[key], { ...emptyContact }]);
  };

  const removeContact = (key: ContactField, index: number) => {
    onChange(
      key,
      payload[key].filter((_, i) => i !== index),
    );
  };

  const addTag = () => {
    if (payload.tagList.length >= MAX_TAGS) return;
    onChange("tagList", [...payload.tagList, ""]);
  };

  const setTag = (index: number, value: string) => {
    onChange(
      "tagList",
      payload.tagList.map((tag, i) => (i === index ? value : tag)),
    );
  };

  const removeTag = (index: number) => {
    onChange(
      "tagList",
      payload.tagList.filter((_, i) => i !== index),
    );
  };

  const renderContacts = (key: ContactField, title: string, placeholder: string) => (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[13px] font-semibold text-gray-500">{title}</span>
        <button
          type="button"
          onClick={() => addContact(key)}
          className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#ffa217] transition hover:opacity-80"
        >
          <Plus size={13} />
          {t("Add")}
        </button>
      </div>
      <div className="space-y-2">
        {payload[key].map((contact, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              value={contact.input1}
              onChange={(e) => setField(key, index, e.target.value, 1)}
              placeholder={t("Title")}
              className="w-2/5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-[12.5px] text-gray-700 outline-none transition focus:border-[#ffa217] focus:bg-white"
            />
            <input
              type="text"
              value={contact.input2}
              onChange={(e) => setField(key, index, e.target.value, 2)}
              placeholder={placeholder}
              className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-[12.5px] text-gray-700 outline-none transition focus:border-[#ffa217] focus:bg-white"
            />
            <button
              type="button"
              onClick={() => removeContact(key, index)}
              aria-label={t("Delete")}
              className="rounded-lg p-1.5 text-gray-300 transition hover:bg-red-50 hover:text-red-500"
            >
              <X size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <label className={labelClass} htmlFor="event-title">
          {t("Title*")}
        </label>
        <input
          id="event-title"
          type="text"
          value={payload.title}
          onChange={(e) => onChange("title", e.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass} htmlFor="event-description">
          {t("Description (optional)")}
        </label>
        <textarea
          id="event-description"
          rows={4}
          value={payload.description}
          onChange={(e) => onChange("description", e.target.value)}
          className={`${inputClass} resize-y`}
        />
      </div>

      <div>
        <label className={labelClass} htmlFor="event-location">
          {t("Location (optional)")}
        </label>
        <input
          id="event-location"
          type="text"
          value={payload.location}
          onChange={(e) => onChange("location", e.target.value)}
          className={inputClass}
        />
      </div>

      <div className="space-y-4 border-t border-gray-100 pt-5">
        {renderContacts("phoneList", t("Phone"), t("Phone number"))}
        {renderContacts("emailList", t("Email"), t("Email"))}
        {renderContacts("linkList", t("Link"), t("Title of the link (optional)"))}
      </div>

      <div className="border-t border-gray-100 pt-5">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-[13px] font-semibold text-gray-500">
            {t("HashTag (max 20.)")}
          </span>
          <button
            type="button"
            onClick={addTag}
            disabled={tagsReadOnly || payload.tagList.length >= MAX_TAGS}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#ffa217] transition hover:opacity-80 disabled:cursor-not-allowed disabled:text-gray-300"
          >
            <Plus size={13} />
            {t("Add")}
          </button>
        </div>
        {tagsReadOnly ? (
          <p className="mb-2 text-[11.5px] text-gray-400">
            {t("Hash tags cannot be changed after the event is created.")}
          </p>
        ) : null}
        {tagsReadOnly ? (
          <div className="flex flex-wrap gap-2">
            {payload.tagList.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-gray-100 px-3 py-1 text-[12px] font-medium text-gray-600"
              >
                #{tag}
              </span>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {payload.tagList.map((tag, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={tag}
                  onChange={(e) => setTag(index, e.target.value)}
                  placeholder={t("Add a tag")}
                  className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-[12.5px] text-gray-700 outline-none transition focus:border-[#ffa217] focus:bg-white"
                />
                <button
                  type="button"
                  onClick={() => removeTag(index)}
                  aria-label={t("Delete")}
                  className="rounded-lg p-1.5 text-gray-300 transition hover:bg-red-50 hover:text-red-500"
                >
                  <X size={15} />
                </button>
              </div>
            ))}
            {payload.tagList.length === 0 ? (
              <p className="text-[12px] text-gray-400">{t("Add a tag")}</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventFormInfoStep;
